import axios from 'axios';

// The blog is public and lives on the same Django API host, so it reuses
// NEXT_PUBLIC_API_URL with a plain axios instance (no JWT / interceptors needed).
const API = process.env.NEXT_PUBLIC_API_URL; // https://api.lovingecards.com/api/v1
// proxy: false skips axios's env-proxy lookup (proxy-from-env → deprecated url.parse, DEP0169).
const api = axios.create({ baseURL: API, timeout: 30000, proxy: false });

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  post_count?: number;
  meta_title?: string;
  meta_description?: string;
}

export interface BlogTag {
  id: string;
  name: string;
  slug: string;
  description?: string;
  post_count?: number;
}

export interface BlogFaq {
  id: string;
  question: string;
  answer: string;                   // plain text or simple HTML
  position?: number;
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;                 // HTML (detail only)
  featured_image?: string | null;
  featured_image_alt?: string | null;
  category?: BlogCategory | null;
  tags?: BlogTag[];
  faqs?: BlogFaq[];                 // detail only — drives FAQ schema + section
  author_name?: string;
  published_at: string;
  updated_at: string;
  created_at?: string;
  reading_time?: number;
  og_image?: string | null;
  meta_title?: string;
  meta_description?: string;
  rating_value?: string | number | null;  // DRF Decimal serialises as string
  rating_count?: number | null;
}

export async function getPosts(params: {
  page?: number;
  per_page?: number;
  category?: string;
  tag?: string;
  search?: string;
} = {}): Promise<{ posts: BlogPost[]; totalPages: number; total: number }> {
  // Retry transient failures (network errors / 5xx) a couple of times before
  // giving up — a brief backend blip or a worker restart shouldn't collapse
  // the whole /blog route into the error screen.
  const MAX_ATTEMPTS = 3;
  let lastError: unknown;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const { data } = await api.get('/blog/posts/', {
        params: {
          page: params.page,
          page_size: params.per_page,
          category: params.category,
          tag: params.tag,
          search: params.search,
        },
      });
      return {
        posts: data.response_data ?? [],
        totalPages: data.pagination?.total_pages ?? 1,
        total: data.pagination?.count ?? 0,
      };
    } catch (error) {
      lastError = error;

      // DRF's paginator returns HTTP 404 ("Invalid page.") for any page beyond
      // the last one. That isn't a real failure — it just means this page has
      // no posts — so treat it as an empty result instead of crashing the
      // route. The caller can redirect an out-of-range page back to page 1.
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return { posts: [], totalPages: 0, total: 0 };
      }

      // Only retry transient errors: no response (network/timeout) or 5xx.
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      const isTransient = status === undefined || status >= 500;
      if (isTransient && attempt < MAX_ATTEMPTS) {
        await new Promise((r) => setTimeout(r, 300 * attempt)); // 300ms, 600ms backoff
        continue;
      }
      break;
    }
  }

  if (axios.isAxiosError(lastError)) {
    console.error(
      `Error fetching posts (GET ${API}/blog/posts/): status=${lastError.response?.status ?? 'no-response'}`,
      lastError.response?.data ?? lastError.message,
    );
  } else {
    console.error('Error fetching posts:', lastError);
  }
  throw new Error('Failed to fetch posts from blog API');
}

export async function getPost(slug: string): Promise<BlogPost | null> {
  try {
    const { data } = await api.get(`/blog/posts/${slug}/`);
    return data.response_status ? data.response_data : null;
  } catch (error) {
    console.error('Error fetching post:', error);
    return null;
  }
}

export async function getCategories(): Promise<BlogCategory[]> {
  try {
    const { data } = await api.get('/blog/categories/');
    return data.response_data ?? [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
}

export async function getCategory(slug: string): Promise<BlogCategory | null> {
  try {
    const { data } = await api.get(`/blog/categories/${slug}/`);
    return data.response_status ? data.response_data : null;
  } catch (error) {
    console.error('Error fetching category:', error);
    return null;
  }
}

export async function getTags(): Promise<BlogTag[]> {
  try {
    const { data } = await api.get('/blog/tags/');
    return data.response_data ?? [];
  } catch (error) {
    console.error('Error fetching tags:', error);
    return [];
  }
}

export async function getTag(slug: string): Promise<BlogTag | null> {
  try {
    const { data } = await api.get(`/blog/tags/${slug}/`);
    return data.response_status ? data.response_data : null;
  } catch (error) {
    console.error('Error fetching tag:', error);
    return null;
  }
}

// --- Utilities ---------------------------------------------------------------
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function extractPlainTextFromHTML(html: string): string {
  return (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * A clean excerpt for cards and meta descriptions.
 *
 * Per content guidelines we never append a trailing "..." and we strip any
 * "[…]" / "Read more" markers. If the text is longer than `maxLength` we trim
 * on a word boundary with no ellipsis.
 */
export function getExcerpt(post: BlogPost, maxLength: number = 160): string {
  let text = (post.excerpt?.trim() || extractPlainTextFromHTML(post.content || '')).trim();

  // Drop trailing read-more markers that can survive in imported excerpts.
  text = text
    .replace(/\[(?:…|\.{2,3}|hellip;?)\]\s*$/i, '')
    .replace(/\(?\bread\s+more\b\)?\.*\s*$/i, '')
    .replace(/(?:…|\.{3})\s*$/, '')
    .trim();

  if (text.length <= maxLength) return text;
  // Trim on a word boundary; no trailing ellipsis.
  return text.slice(0, maxLength).replace(/\s+\S*$/, '').replace(/[,;:.\-\s]+$/, '');
}
